import { groupBy, keys, mapValues, sortBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import { PermissionButton } from '../../../components/Button/PermissionButton';
import { Card } from '../../../components/Card/Card';
import { useConfirmation } from '../../../components/ConfirmationPrompt/ConfirmationPrompt';
import { Icon } from '../../../components/Icon/Icon';
import { Modal } from '../../../components/Modal/Modal';
import { SortableList } from '../../../components/SortableList/SortableList';
import { Stack } from '../../../components/Stack/Stack';
import { StatusPill } from '../../../components/StatusPill/StatusPill';
import { t } from '../../../locales/translate';
import { useRemoteData } from '../../../model/data';
import { isAdmin, useSelectedMembership } from '../../../model/membership';
import { applyTax } from '../../../model/price';
import { Price, PriceUpdate, Product, deleteProductPrice, getProduct } from '../../../model/product';
import { getAllShops, getShopName } from '../../../model/shop';
import { formatCurrency, formatDateTime, formatPercentage, useErrorHandler } from '../../../util/util';
import { EditVat } from './Modals/EditVat/EditVat';
import { PricingUpdateForm } from './Modals/PricingUpdateForm';
import './Pricing.css';
import './ProductDetails.css';

interface Props {
  product: Product;
  onPriceChange: () => void;
}

export function Pricing({ product, onPriceChange }: Props): JSX.Element {
  const membership = useSelectedMembership();
  const admin = isAdmin(membership);
  // create
  const [displayEditVatModal, setDisplayEditVatModal] = useState(false);
  // update
  const [selectedPriceForEdit, setSelectedPriceForEdit] = useState<PriceUpdate | undefined | null>(undefined); // null representing a new price
  // delete
  const [selectedPriceForDelete, setSelectedPriceForDelete] = useState<Price | undefined>(undefined);

  const productVariants =
    product.productVariants && product.productVariants.length > 0 ? product.productVariants : undefined;
  const prices: Price[] = productVariants?.[0]?.prices ?? [];

  const [shops, , shopsError] = useRemoteData(
    async () => {
      if (admin || prices.find((p) => p.shopId)) {
        return await getAllShops();
      }
    },
    (db) => db.table('shops').all
  );

  // If the shops endpoint is not authorized, do not log error and simply leave the shops being empty. A "-" will appear instead of the shop name.
  useErrorHandler(shopsError?.['response']?.['status'] === 403 ? undefined : shopsError);

  const [pricesViewModels, setPricesViewModels] = useState<PricesViewModel[] | undefined>([]);
  useEffect(() => setPricesViewModels(transformPricesToViewModel(prices, product)), [prices, product, shops]);

  async function handleDeletePrice(): Promise<void> {
    if (selectedPriceForDelete) {
      await deleteProductPrice(selectedPriceForDelete.id);
      setSelectedPriceForDelete(undefined);
      onPriceChange();
      await getProduct(product.id);
    }
  }
  const { confirm: confirmPriceDelete } = useConfirmation({
    title: t.actions.delete(t.model.price._name),
    message: t.general.confirmDelete(t.model.price._name),
    action: handleDeletePrice,
    onClose: () => setSelectedPriceForDelete(undefined),
  });

  useEffect(() => {
    if (selectedPriceForDelete) {
      confirmPriceDelete();
    }
  }, [selectedPriceForDelete]);

  return (
    <>
      <Card
        title={t.model.price._verb}
        actions={
          productVariants && (
            <>
              <PermissionButton
                name="money"
                onClick={() => setDisplayEditVatModal(true)}
                model={product}
                permission="isAdmin"
                tooltip={t.model.price.editTaxes}
              />
              <PermissionButton
                name="plus"
                onClick={() => setSelectedPriceForEdit(null)}
                model={product}
                permission="isAdmin"
                tooltip={t.model.price.createPrice}
              />
            </>
          )
        }
        fullbleed={productVariants && prices && prices.length > 0}
      >
        {!productVariants && t.model.productVariant.pricingError}
        {productVariants &&
          (!prices || prices.length === 0) &&
          t.errors.noItemsforThisCollection(t.model.price._plural, t.model.product._name)}
        {productVariants && prices.length > 0 && (
          <SortableList
            className="product-pricing__list"
            items={pricesViewModels}
            columns={[
              {
                width: 100,
                label: t.model.shop._name,
                dataKey: 'shop',
                rowSpan: (row) => row.countInShop || 1,
                cellClassNames: (row) => [
                  row.countInShop ? 'product-pricing__shop-name-cell' : 'product-pricing__shop-name-cell--hidden',
                ],
              },
              {
                label: t.model.datetime.timeframe,
                dataKey: 'activeFrom',
                cellRenderer: (row) => (
                  <div className="product-pricing__timeframe">
                    <div>{row.activeFrom ? formatDateTime(row.activeFrom) : t.model.datetime.eternity}</div>
                    <div>{row.activeUntil ? formatDateTime(row.activeUntil) : t.model.datetime.eternity}</div>
                  </div>
                ),
              },
              {
                label: t.model.price.netPrice,
                dataKey: 'priceNet',
                cellRenderer: (row) => (
                  <Stack size="zero">
                    {row.priceOriginal && <span className="strikethrough">{row.priceOriginal}</span>}
                    <span>{row.priceNet}</span>
                  </Stack>
                ),
              },
              {
                label: 'VAT',
                dataKey: 'vat',
              },
              {
                label: t.model.price.grossPrice,
                dataKey: 'priceGross',
                cellRenderer: (row) => (
                  <Stack size="zero">
                    {row.priceOriginalGross && <span className="strikethrough">{row.priceOriginalGross}</span>}
                    <span>{row.priceGross}</span>
                  </Stack>
                ),
              },
              pricesViewModels?.some((e) => e.overlayListing)
                ? {
                    label: t.model.product.overlayListing,
                    dataKey: 'overlayListing',
                    cellRenderer: (row) => (
                      <span>
                        {row.overlayListing ? (
                          <img height="32px" width="32px" style={{ objectFit: 'contain' }} src={row.overlayListing} />
                        ) : (
                          <Icon name="image" />
                        )}
                      </span>
                    ),
                  }
                : null,
              pricesViewModels?.some((e) => e.overlayDetail)
                ? {
                    label: t.model.product.overlayDetail,
                    dataKey: 'overlayDetail',
                    cellRenderer: (row) => (
                      <span>
                        {row.overlayDetail ? (
                          <img height="32px" width="32px" style={{ objectFit: 'contain' }} src={row.overlayDetail} />
                        ) : (
                          <Icon name="image" />
                        )}
                      </span>
                    ),
                  }
                : null,
              pricesViewModels?.some((e) => e.comment?.length > 1)
                ? {
                    label: t.model.tour.comment,
                    dataKey: 'comment',
                    ellipsisOverflow: true,
                    maxWidth: '90px',
                  }
                : null,
              {
                label: t.model.status._name,
                dataKey: 'active',
                cellRenderer: (row) => (
                  <StatusPill
                    status={row.active ? 'active' : 'inactive'}
                    label={row.active ? t.model.price.active : t.model.price.inactive}
                  />
                ),
              },
              {
                label: '',
                dataKey: 'edit',
                cellRenderer: (row) => (
                  <div className="product-pricing__actions">
                    <PermissionButton
                      model={row.price}
                      permission="update"
                      onClick={() => {
                        setSelectedPriceForEdit({
                          ...row.price,
                          shopId: row.price.shopId ?? undefined,
                          activeFrom: row.price.activeFrom ?? undefined,
                          activeUntil: row.price.activeUntil ?? undefined,
                        });
                      }}
                      name="edit"
                      hideLock={!admin}
                    />
                    <PermissionButton
                      model={row.price}
                      permission="destroy"
                      onClick={() => setSelectedPriceForDelete(row.price)}
                      name="trash"
                      hideLock={!admin}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>
      <Modal open={displayEditVatModal}>
        <EditVat
          productId={product.id}
          onClose={() => setDisplayEditVatModal(false)}
          onTaxUpdate={() => {
            getProduct(product.id);
          }}
        />
      </Modal>

      <Modal
        open={selectedPriceForEdit !== undefined}
        title={selectedPriceForEdit === null ? t.model.price.createPrice : t.actions.edit(t.model.price._name)}
        size="small"
      >
        {() =>
          selectedPriceForEdit !== undefined && (
            <PricingUpdateForm
              productId={product.id}
              shops={shops}
              price={selectedPriceForEdit ?? null}
              vatRate={product.taxesPerCountry?.de}
              onClose={() => {
                setSelectedPriceForEdit(undefined);
                onPriceChange();
              }}
            />
          )
        }
      </Modal>
    </>
  );
}

interface PricesViewModel {
  countInShop?: number;
  price: Price;
  shop: string;
  activeFrom: Date | null;
  activeUntil: Date | null;
  priceNet: string;
  vat: string;
  priceGross: string;
  priceOriginal?: string;
  priceOriginalGross?: string;
  overlayListing: string | undefined;
  overlayDetail: string | undefined;
  comment: string;
  active: boolean;
  edit: string;
}

// TODO implement a filter function to match the state in the ListSearch
function priceFilterFunction(price: Price): boolean {
  return !!price;
}

function transformPricesToViewModel(prices: Price[], product: Product): PricesViewModel[] | undefined {
  const vatRate = product.taxesPerCountry?.de;
  const pre = prices.filter(priceFilterFunction).map((v) => {
    return {
      countInShop: undefined,
      price: v,
      shop: v.shopId ? getShopName(v.shopId) || '-' : t.model.shop.allShops,
      activeFrom: v.activeFrom,
      activeUntil: v.activeUntil,
      priceNet: formatCurrency(v.amount),
      vat: formatPercentage(vatRate) || '-',
      priceGross: vatRate && formatCurrency(applyTax(v.amount, vatRate)),
      priceOriginal: formatCurrency(v.originalPrice),
      priceOriginalGross: vatRate && v.originalPrice && formatCurrency(applyTax(v.originalPrice, vatRate)),
      overlayListing: v.overlayListingImage?.url,
      overlayDetail: v.overlayDetailImage?.url,
      comment: v.comment || '-',
      active: v.active,
      edit: 'edit', // TODO: Translate?
    } as PricesViewModel;
  });
  const byShop = mapValues(groupBy(pre, 'shop'), (v) => {
    const sorted = sortBy(v, ['activeFrom', 'activeUntil', 'amount']);
    sorted[0].countInShop = sorted.length;
    return sorted;
  });

  // Start with the prices for all shops
  const priceViewModels = byShop[t.model.shop.allShops];
  // Then add prices, grouped by shops, by alphabetical shop name order.
  keys(byShop)
    .filter((k) => k !== t.model.shop.allShops)
    .sort()
    .forEach((shopKey) => priceViewModels.push(...byShop[shopKey]));

  return priceViewModels;
}
